// src/pages/SuperAdmin/DropdownsManager.jsx
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Spinner
} from 'reactstrap';
import { get, post, put, del } from 'helpers/api_helper';
import { DROPDOWN_CATEGORIES, invalidateDropdownCache } from 'helpers/dropdownHelper';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import {
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiSearchLine,
  RiRefreshLine,
  RiCheckLine,
  RiCloseLine,
  RiListSettingsLine,
  RiShieldCheckLine,
  RiToothLine,
  RiStethoscopeLine,
  RiTimeLine,
  RiMedicineBottleLine,
  RiCalendarEventLine,
  RiHeartPulseLine,
  RiFolder2Line,
  RiDropLine,
  RiUser3Line,
  RiGenderlessLine,
  RiCalendarCheckLine,
  RiShareForwardLine,
  RiMoneyDollarCircleLine,
  RiPulseLine
} from 'react-icons/ri';

const categoryIcons = {
  dental_chart_examination: <RiToothLine />,
  treatment_category_type: <RiStethoscopeLine />,
  rx_frequency: <RiTimeLine />,
  rx_dosage: <RiMedicineBottleLine />,
  rx_duration: <RiCalendarEventLine />,
  auscultation: <RiHeartPulseLine />,
  file_category: <RiFolder2Line />,
  blood_group: <RiDropLine />,
  salutation: <RiUser3Line />,
  gender: <RiGenderlessLine />,
  appointment_status: <RiCalendarCheckLine />,
  referral_source: <RiShareForwardLine />,
  payment_mode: <RiMoneyDollarCircleLine />,
  clinical_symptoms: <RiPulseLine />,
};

const DropdownsManager = ({ initialCategory = null, onCategoryChange = null }) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || DROPDOWN_CATEGORIES[0].key);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [categorySearch, setCategorySearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    category: DROPDOWN_CATEGORIES[0].key,
    label: '',
    value: '',
    group_name: '',
    sort_order: 0,
    is_active: true,
    extra_json: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Sync if initialCategory prop changes
  useEffect(() => {
    if (initialCategory && initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);

  const handleSelectCategory = (key) => {
    setSelectedCategory(key);
    if (onCategoryChange) {
      onCategoryChange(key);
    }
  };

  const fetchCategoryCounts = async () => {
    try {
      const res = await get('/superadmin/dropdowns/categories');
      if (res?.success && Array.isArray(res.body)) {
        const counts = {};
        res.body.forEach((c) => {
          counts[c.category] = c.count;
        });
        setCategoryCounts(counts);
      }
    } catch (e) {
      console.warn('Could not fetch category counts:', e);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await get(`/superadmin/dropdowns?include_inactive=true&category=${selectedCategory}`);
      if (res?.success && Array.isArray(res.body)) {
        setItems(res.body);
        setCategoryCounts((prev) => ({
          ...prev,
          [selectedCategory]: res.body.length,
        }));
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Failed to load dropdown items:', err);
      showErroeAlert('Failed to load dropdown items from server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryCounts();
  }, []);

  useEffect(() => {
    fetchItems();
  }, [selectedCategory]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setEditingId(null);
    setFormData({
      category: selectedCategory,
      label: '',
      value: '',
      group_name: '',
      sort_order: items.length + 1,
      is_active: true,
      extra_json: '',
    });
    setModalOpen(true);
  };

  const handleOpenEditModal = (item) => {
    setIsEditing(true);
    setEditingId(item.id);
    setFormData({
      category: item.category,
      label: item.label,
      value: item.value,
      group_name: item.group_name || '',
      sort_order: item.sort_order || 0,
      is_active: Boolean(item.is_active),
      extra_json: item.extra_data ? JSON.stringify(item.extra_data, null, 2) : '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    let parsedExtra = null;
    if (formData.extra_json.trim()) {
      try {
        parsedExtra = JSON.parse(formData.extra_json);
      } catch (err) {
        showErroeAlert('Invalid JSON in Extra Metadata field');
        setSubmitting(false);
        return;
      }
    }

    const payload = {
      category: formData.category,
      label: formData.label,
      value: formData.value || formData.label.toLowerCase().replace(/\s+/g, '_'),
      group_name: formData.group_name || null,
      sort_order: Number(formData.sort_order) || 0,
      is_active: formData.is_active,
      extra_data: parsedExtra,
    };

    try {
      if (isEditing) {
        const res = await put(`/superadmin/dropdowns/${editingId}`, payload);
        if (res?.success) {
          showSuccessAlert('Dropdown item updated successfully');
          invalidateDropdownCache();
          setModalOpen(false);
          fetchItems();
          fetchCategoryCounts();
        }
      } else {
        const res = await post('/superadmin/dropdowns', payload);
        if (res?.success) {
          showSuccessAlert('New dropdown item created successfully');
          invalidateDropdownCache();
          setModalOpen(false);
          fetchItems();
          fetchCategoryCounts();
        }
      }
    } catch (err) {
      console.error('Error saving dropdown item:', err);
      showErroeAlert(err?.message || 'Failed to save dropdown item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const nextStatus = !item.is_active;
      const res = await put(`/superadmin/dropdowns/${item.id}`, { is_active: nextStatus });
      if (res?.success) {
        showSuccessAlert(`Option "${item.label}" is now ${nextStatus ? 'Active' : 'Disabled'}`);
        invalidateDropdownCache();
        fetchItems();
      }
    } catch (err) {
      showErroeAlert('Failed to update option status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.label}"?`)) {
      return;
    }

    try {
      const res = await del(`/superadmin/dropdowns/${item.id}`);
      if (res?.success) {
        showSuccessAlert('Dropdown option removed');
        invalidateDropdownCache();
        fetchItems();
        fetchCategoryCounts();
      }
    } catch (err) {
      showErroeAlert('Failed to delete dropdown option');
    }
  };

  const filteredCategories = DROPDOWN_CATEGORIES.filter((cat) => {
    if (!categorySearch.trim()) return true;
    const term = categorySearch.toLowerCase();
    return cat.label.toLowerCase().includes(term) || cat.key.toLowerCase().includes(term);
  });

  const filteredItems = items.filter((item) => {
    if (statusFilter === 'active' && !item.is_active) return false;
    if (statusFilter === 'inactive' && item.is_active) return false;
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();
    return (
      item.label?.toLowerCase().includes(term) ||
      item.value?.toLowerCase().includes(term) ||
      item.group_name?.toLowerCase().includes(term)
    );
  });

  const currentCategoryMeta = DROPDOWN_CATEGORIES.find((c) => c.key === selectedCategory) || DROPDOWN_CATEGORIES[0];
  const activeCount = items.filter((i) => i.is_active).length;
  const disabledCount = items.length - activeCount;

  return (
    <div className="dropdowns-manager">
      <Row className="g-3">
        {/* Left Column: Master Categories Sidebar (3.5 / 12) */}
        <Col lg={4} md={5}>
          <div className="category-sidebar">
            <div className="sidebar-header">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="font-size-14 font-weight-bold text-dark mb-0 d-flex align-items-center gap-1.5">
                  <RiListSettingsLine className="text-primary font-size-16" /> Master Catalogs
                </h6>
                <Badge color="light" className="text-muted border font-size-11 font-weight-medium">
                  {DROPDOWN_CATEGORIES.length} Categories
                </Badge>
              </div>
              <div className="position-relative">
                <Input
                  type="text"
                  className="form-control form-control-sm ps-4 font-size-12"
                  placeholder="Filter categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
                <RiSearchLine className="position-absolute text-muted" style={{ left: '10px', top: '9px', fontSize: '13px' }} />
              </div>
            </div>

            <div className="category-list">
              {filteredCategories.map((cat) => {
                const isSelected = selectedCategory === cat.key;
                const count = categoryCounts[cat.key] ?? (isSelected ? items.length : '-');
                return (
                  <div
                    key={cat.key}
                    className={`cat-nav-item ${isSelected ? 'active' : ''}`}
                    onClick={() => handleSelectCategory(cat.key)}
                  >
                    <div className="d-flex align-items-center gap-2.5 overflow-hidden">
                      <div className="cat-icon">
                        {categoryIcons[cat.key] || <RiListSettingsLine />}
                      </div>
                      <div className="overflow-hidden">
                        <div className="font-size-13 font-weight-bold text-truncate">{cat.label}</div>
                        <div className="text-muted font-size-11 text-truncate">{cat.description}</div>
                      </div>
                    </div>
                    <Badge
                      color={isSelected ? 'warning' : 'light'}
                      className={`font-size-11 font-weight-semibold px-2 py-0.5 rounded-pill border ${
                        isSelected ? 'bg-warning text-dark' : 'text-muted'
                      }`}
                    >
                      {count}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>
        </Col>

        {/* Right Column: Selected Category Details & Options Table (8.5 / 12) */}
        <Col lg={8} md={7}>
          <div className="master-details-card">
            {/* Details Banner Header */}
            <div className="details-header d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center"
                  style={{ width: '48px', height: '48px', backgroundColor: 'rgba(248, 202, 21, 0.18)', color: '#854D0E', fontSize: '24px' }}
                >
                  {categoryIcons[currentCategoryMeta.key] || <RiListSettingsLine />}
                </div>
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="font-size-16 font-weight-bold text-dark mb-0">{currentCategoryMeta.label}</h5>
                    <code className="text-muted font-size-11 font-weight-normal">{currentCategoryMeta.key}</code>
                  </div>
                  <p className="text-muted font-size-12 mb-0 mt-0.5">{currentCategoryMeta.description}</p>
                </div>
              </div>

              <Button
                color="primary"
                size="sm"
                className="font-size-12 d-inline-flex align-items-center gap-1.5 font-weight-semibold shadow-xs px-3 py-1.5"
                onClick={handleOpenAddModal}
              >
                <RiAddLine className="font-size-16" /> Add Option
              </Button>
            </div>

            {/* Toolbar: Search, Status Filter & Quick Metrics */}
            <div className="details-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="badge bg-white text-dark border font-size-11 px-2 py-1 font-weight-semibold">
                  Total: <strong>{items.length}</strong>
                </span>
                <span className="badge bg-soft-success text-success border border-success-subtle font-size-11 px-2 py-1 font-weight-semibold">
                  Active: <strong>{activeCount}</strong>
                </span>
                {disabledCount > 0 && (
                  <span className="badge bg-soft-secondary text-secondary border font-size-11 px-2 py-1 font-weight-semibold">
                    Disabled: <strong>{disabledCount}</strong>
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="position-relative" style={{ width: '200px' }}>
                  <Input
                    type="text"
                    className="form-control form-control-sm ps-4 font-size-12"
                    placeholder="Search options..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <RiSearchLine className="position-absolute text-muted" style={{ left: '10px', top: '9px', fontSize: '13px' }} />
                </div>

                <Input
                  type="select"
                  className="form-select form-select-sm font-size-12"
                  style={{ width: '120px' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Disabled Only</option>
                </Input>

                <Button
                  color="light"
                  size="sm"
                  className="border font-size-12 d-inline-flex align-items-center"
                  onClick={fetchItems}
                  disabled={loading}
                  title="Refresh options"
                >
                  <RiRefreshLine className={loading ? 'bx-spin' : ''} />
                </Button>
              </div>
            </div>

            {/* Options Table */}
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="sm" />
                  <span className="ms-2 text-muted font-size-13">Loading master options...</span>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <RiListSettingsLine className="font-size-36 d-block mx-auto mb-2 opacity-40 text-secondary" />
                  <h6 className="font-weight-semibold text-dark mb-1">No options found</h6>
                  <p className="font-size-12 mb-3">No master dropdown options match your query.</p>
                  <Button color="primary" size="sm" onClick={handleOpenAddModal}>
                    <RiAddLine /> Create First Option
                  </Button>
                </div>
              ) : (
                <Table hover className="mb-0 align-middle font-size-13">
                  <thead className="table-light border-bottom">
                    <tr>
                      <th style={{ width: '55px' }} className="text-center">#</th>
                      <th>Display Label</th>
                      <th>Internal Slug / Value</th>
                      <th>Sub-Group</th>
                      <th>Metadata</th>
                      <th style={{ width: '110px' }} className="text-center">Status</th>
                      <th style={{ width: '100px' }} className="text-end pe-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item, idx) => (
                      <tr key={item.id}>
                        <td className="text-center text-muted font-weight-semibold font-size-12">
                          {item.sort_order || idx + 1}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1.5 flex-wrap">
                            <span className="font-weight-bold text-dark font-size-13">{item.label}</span>
                            {item.is_system ? (
                              <span className="badge bg-light text-muted border font-size-10 font-weight-normal px-1.5 py-0.5">
                                System
                              </span>
                            ) : (
                              <span className="badge bg-soft-primary text-primary font-size-10 font-weight-medium px-1.5 py-0.5">
                                Custom
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="mono-code-chip">{item.value}</span>
                        </td>
                        <td>
                          {item.group_name ? (
                            <Badge color="info" className="font-size-11 font-weight-medium bg-soft-info text-info border border-info-subtle">
                              {item.group_name}
                            </Badge>
                          ) : (
                            <span className="text-muted font-size-11">-</span>
                          )}
                        </td>
                        <td>
                          {item.extra_data ? (
                            <span className="badge bg-light text-dark border font-size-11 font-monospace" title={JSON.stringify(item.extra_data)}>
                              {JSON.stringify(item.extra_data).length > 25
                                ? `${JSON.stringify(item.extra_data).substring(0, 25)}...`
                                : JSON.stringify(item.extra_data)}
                            </span>
                          ) : (
                            <span className="text-muted font-size-11">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span
                            className={`badge-status-pill ${item.is_active ? 'active' : 'disabled'}`}
                            onClick={() => handleToggleStatus(item)}
                            title="Click to toggle active status"
                          >
                            {item.is_active ? (
                              <>
                                <RiCheckLine /> Active
                              </>
                            ) : (
                              <>
                                <RiCloseLine /> Disabled
                              </>
                            )}
                          </span>
                        </td>
                        <td className="text-end pe-3">
                          <div className="d-flex justify-content-end gap-1.5">
                            <button
                              type="button"
                              className="btn-action-icon btn-edit"
                              onClick={() => handleOpenEditModal(item)}
                              title="Edit option"
                            >
                              <RiEditLine />
                            </button>
                            <button
                              type="button"
                              className="btn-action-icon btn-delete"
                              onClick={() => handleDelete(item)}
                              title="Delete option"
                            >
                              <RiDeleteBinLine />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Add / Edit Option Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} centered>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={() => setModalOpen(!modalOpen)} className="bg-light py-2.5 px-3">
            <span className="font-weight-bold text-dark font-size-14 d-flex align-items-center gap-2">
              <RiListSettingsLine className="text-primary" />
              {isEditing ? 'Edit Dropdown Option' : 'Add New Dropdown Option'}
            </span>
          </ModalHeader>
          <ModalBody className="p-3">
            <FormGroup className="mb-2.5">
              <Label className="font-weight-semibold font-size-12">Target Category</Label>
              <Input
                type="select"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                disabled={isEditing}
                className="font-size-13"
              >
                {DROPDOWN_CATEGORIES.map((cat) => (
                  <option key={cat.key} value={cat.key}>
                    {cat.label} ({cat.key})
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup className="mb-2.5">
              <Label className="font-weight-semibold font-size-12">Display Label *</Label>
              <Input
                type="text"
                required
                className="font-size-13"
                placeholder="e.g. Periapical Abscess"
                value={formData.label}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    label: val,
                    value: isEditing ? prev.value : val.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
                  }));
                }}
              />
            </FormGroup>

            <Row>
              <Col md={7}>
                <FormGroup className="mb-2.5">
                  <Label className="font-weight-semibold font-size-12">Internal Value / Slug *</Label>
                  <Input
                    type="text"
                    required
                    className="font-size-13 font-monospace"
                    placeholder="e.g. periapical_abscess"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md={5}>
                <FormGroup className="mb-2.5">
                  <Label className="font-weight-semibold font-size-12">Sort Order</Label>
                  <Input
                    type="number"
                    className="font-size-13"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-2.5">
              <Label className="font-weight-semibold font-size-12">Sub-Group / Classification (Optional)</Label>
              <Input
                type="text"
                className="font-size-13"
                placeholder="e.g. Restorative Treatments, Oral Surgery"
                value={formData.group_name}
                onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
              />
            </FormGroup>

            <FormGroup className="mb-2.5">
              <Label className="font-weight-semibold font-size-12">Extra Metadata JSON (Optional)</Label>
              <Input
                type="textarea"
                rows="2"
                placeholder='e.g. { "tooth": "caries" } or { "color": "primary" }'
                value={formData.extra_json}
                onChange={(e) => setFormData({ ...formData, extra_json: e.target.value })}
                className="font-monospace font-size-12"
              />
            </FormGroup>

            <FormGroup check className="mt-2.5">
              <Label check className="font-size-12 font-weight-semibold cursor-pointer">
                <Input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />{' '}
                Enable option in clinical dropdowns
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter className="py-2 px-3">
            <Button color="light" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button color="primary" size="sm" type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : isEditing ? 'Update Option' : 'Create Option'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default DropdownsManager;
